import { createClient } from '@supabase/supabase-js';
import { BACKEND_URL, BACKEND_ANON_KEY } from '@/config/backend';
import { BASE_PATH } from '@/lib/basePath';
import {
  isMaintenanceBlocking,
  markMaintenanceBlocked,
  MaintenanceBlockedError,
} from '@/lib/maintenance/maintenanceState';

// M31 — the backend address + anon key are read in a single place
// (src/config/backend.ts). Keep the existing names/exports here so every
// current importer continues to work unchanged.
const supabaseUrl = BACKEND_URL;
export const supabaseAnonKey = BACKEND_ANON_KEY;

export { supabaseUrl };

// M32 — the Edge Function URL helper now lives in a single module:
//   import { getEdgeFunctionUrl } from '@/lib/edge/functions';

// Request metadata captured from the most recent HTTP response, used to
// surface real status codes / request ids in error messages.
export interface RequestMeta {
  status?: number;
  requestId?: string;
}

let lastRequestMeta: RequestMeta = {};

export function getLastRequestMeta(): RequestMeta {
  return lastRequestMeta;
}

/**
 * 693 [2a]: true when a request failed with HTTP 401. PostgREST returns 401
 * ONLY for a rejected JWT (RLS/permission failures are 403), so a 401 on a data
 * request means the session itself ended. Screens use this to SKIP their generic
 * "couldn't save" toast when the dedicated session-expiry notice is (or is about
 * to be) shown — otherwise the two messages contradict each other on screen.
 */
export function isSessionExpiryError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  return (error as { status?: number }).status === 401;
}

/**
 * True when an auth error means the stored refresh token is permanently
 * unusable — revoked, already used, or unknown to the server.
 *
 * This is the single-use refresh-token rotation race / "signed out on another
 * device" case. It is a GENUINE "please sign in again" condition — not a
 * network failure and not an app bug. Callers use it to route the user into the
 * existing session-expiry flow and to keep this EXPECTED rejection out of the
 * error log (supabase-js rejects its internal token-refresh request, so the
 * rejection would otherwise surface as an uncaught error).
 */
export function isInvalidRefreshTokenError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const code = (error as { code?: string }).code;
  if (
    code === 'refresh_token_not_found'
    || code === 'refresh_token_already_used'
    || code === 'invalid_refresh_token'
  ) {
    return true;
  }
  const message = (error as { message?: string }).message ?? '';
  return /invalid refresh token|refresh token not found|refresh token already used/i.test(message);
}

function readRequestId(headers: Headers): string | undefined {
  const candidates = [
    'x-amzn-requestid',
    'x-amz-request-id',
    'x-request-id',
    'x-supabase-request-id',
    'x-correlation-id',
  ];
  for (const name of candidates) {
    const value = headers.get(name);
    if (value) return value;
  }
  return undefined;
}

// Determines whether a request targets Supabase Storage object endpoints
// (upload / download / signed URL). These are the ONLY requests exempt from
// the 8s timeout + retry logic below (they get a dedicated 30-minute timeout
// instead).
function isStorageRequest(input: Parameters<typeof fetch>[0]): boolean {
  const url =
    typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.href
        : typeof (input as Request)?.url === 'string'
          ? (input as Request).url
          : '';
  return url.includes('/storage/v1/object/');
}

// Auth endpoints (token refresh, sign-in/out, user) are excluded from the 401
// session-expiry check so a failing refresh never re-triggers itself.
function isAuthEndpoint(input: Parameters<typeof fetch>[0]): boolean {
  return getRequestUrl(input).includes('/auth/v1/');
}

// The request URL as a string (string | URL | Request all normalised).
function getRequestUrl(input: Parameters<typeof fetch>[0]): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  const url = (input as Request)?.url;
  return typeof url === 'string' ? url : '';
}

// M66 — storage sub-paths that are POST but are READS; they must never be
// blocked by the maintenance write guard.
const STORAGE_READ_PREFIXES = ['sign/', 'list/', 'info/', 'public/', 'authenticated/'];

// M66 — write-only edge functions (invites). Reads via edge functions are left
// untouched, so only this explicit set is blocked.
const BLOCKED_WRITE_FUNCTIONS = ['send-trip-invite', 'send-friend-invite'];

/**
 * M66 — classify a request as a *blockable write* while maintenance mode is on.
 *
 * Only writes are blocked; EVERY read (GET/HEAD) passes through untouched, and
 * the POST-based read endpoints (PostgREST `rpc`, storage `sign`/`list`/`info`)
 * are explicitly allowed so the app keeps working in view-only mode.
 */
function isBlockableWrite(
  input: Parameters<typeof fetch>[0],
  init: Parameters<typeof fetch>[1],
): boolean {
  const url = getRequestUrl(input);
  if (!url) return false;
  const method = getRequestMethod(input, init);
  if (method === 'GET' || method === 'HEAD') return false;

  // Auth: only account CREATION (signup) is a blocked write. Login, token
  // refresh, sign-out, user, otp, recover all stay available so people can still
  // sign in and out (and admins can work) during maintenance.
  if (url.includes('/auth/v1/')) return url.includes('/auth/v1/signup');

  // PostgREST: rpc() is POST but frequently read-only → never block it.
  if (url.includes('/rest/v1/')) {
    return !url.includes('/rest/v1/rpc/');
  }

  // Storage: uploads / deletes / move / copy are writes; signed-url, listing and
  // info are POST reads and must stay available.
  if (url.includes('/storage/v1/object/')) {
    const after = url.split('/storage/v1/object/')[1] ?? '';
    return !STORAGE_READ_PREFIXES.some((prefix) => after.startsWith(prefix));
  }

  // Edge functions: block only the explicit write-only invite functions.
  if (url.includes('/functions/v1/')) {
    return BLOCKED_WRITE_FUNCTIONS.some((fn) => url.includes(`/functions/v1/${fn}`));
  }

  return false;
}

// Debounce the expiry check so a burst of parallel 401s triggers only one
// refresh attempt, and never fires for auth endpoints (avoids a refresh loop).
let sessionExpiryCheckInFlight = false;

// Tracks whether a recent network-level failure was seen, so we only fire a
// "back online" signal after an actual outage (avoids a CustomEvent on every
// successful request during normal operation).
let networkErrorSeen = false;

function signalNetworkError() {
  if (typeof window === 'undefined') return;
  if (!networkErrorSeen) {
    networkErrorSeen = true;
    window.dispatchEvent(new CustomEvent('elkaway:network-error'));
  }
}

function signalNetworkSuccess() {
  if (typeof window === 'undefined') return;
  if (networkErrorSeen) {
    networkErrorSeen = false;
    window.dispatchEvent(new CustomEvent('elkaway:network-success'));
  }
}

// The HTTP method of a request, defaulting to GET when unspecified.
function getRequestMethod(
  input: Parameters<typeof fetch>[0],
  init: Parameters<typeof fetch>[1],
): string {
  if (init?.method) return init.method.toUpperCase();
  if (typeof input === 'object' && input && 'method' in input) {
    return String((input as Request).method).toUpperCase();
  }
  return 'GET';
}

// A write is any non-read method. A 401 on a WRITE means the token was rejected
// mid-action — the exact "session ended while you were typing" case the draft
// snapshot exists for.
function isWriteRequest(
  input: Parameters<typeof fetch>[0],
  init: Parameters<typeof fetch>[1],
): boolean {
  const method = getRequestMethod(input, init);
  return method !== 'GET' && method !== 'HEAD';
}

// On a 401 the access token may have simply expired (the auto-refresh timer is
// throttled in background tabs). Refresh once; if that fails the refresh token
// is gone → genuine session expiry. If the refresh SUCCEEDS and the request was
// a WRITE, retry it ONCE with the fresh token so a save is not lost to a merely
// stale token. Returns the retry's Response when it happened, else null.
async function maybeHandleAuthFailure(
  input: Parameters<typeof fetch>[0],
  init: Parameters<typeof fetch>[1],
): Promise<Response | null> {
  if (sessionExpiryCheckInFlight || isAuthEndpoint(input)) return null;
  sessionExpiryCheckInFlight = true;
  try {
    const { data, error } = await supabase.auth.refreshSession();
    // Only a genuine auth rejection (invalid / revoked / expired refresh token)
    // means the session is truly over. Network failures and transient errors
    // carry no `status` and must NOT log the user out.
    const status = (error as { status?: number } | null | undefined)?.status;
    if (error && (status === 400 || status === 401)) {
      window.dispatchEvent(new CustomEvent('elkaway:session-expired'));
      return null;
    }

    const token = data?.session?.access_token;
    if (!token || !isWriteRequest(input, init)) return null;

    // Re-issue the rejected write with the refreshed token.
    const headers = new Headers((init?.headers as Record<string, string>) ?? undefined);
    headers.set('Authorization', `Bearer ${token}`);
    return await fetch(input, {
      ...init,
      headers,
      signal: init?.signal ?? AbortSignal.timeout(8000),
    });
  } catch {
    // Network failure while refreshing is not expiry — ignore.
    return null;
  } finally {
    sessionExpiryCheckInFlight = false;
  }
}

// Custom fetch with:
//  - a hard 8s timeout on EVERY request (no more infinite hangs)
//  - retry ONLY for transient network errors (timeouts are surfaced, not retried)
//  - request-id capture for error diagnosis
//  - Storage object requests are EXEMPT from the 8s timeout and the retry
//    loop, and instead get a dedicated 30-minute timeout (uploads can be up
//    to 100MB and cannot complete in 8s; retrying would re-send the payload).
function createRetryFetch(): typeof fetch {
  const MAX_RETRIES = 2;
  const RETRY_DELAY_MS = 500;
  const TIMEOUT_MS = 8000;
  // Storage uploads (up to 100MB) cannot finish within the 8s REST timeout.
  // A dedicated 30-minute ceiling stops uploads from hanging forever when the
  // network drops, while leaving the 8s REST/edge timeout untouched.
  const STORAGE_TIMEOUT_MS = 30 * 60 * 1000;

  return async (...args: Parameters<typeof fetch>) => {
    const [input, init] = args;

    // M66 — maintenance mode: block writes for non-admins at the single data
    // choke point. Reads, storage signed-urls and rpc stay untouched. When
    // maintenance is OFF this is a no-op, so behaviour is unchanged.
    if (isMaintenanceBlocking() && isBlockableWrite(input, init)) {
      markMaintenanceBlocked();
      throw new MaintenanceBlockedError();
    }

    if (isStorageRequest(input)) {
      try {
        const response = await fetch(input, {
          ...init,
          signal: init?.signal ?? AbortSignal.timeout(STORAGE_TIMEOUT_MS),
        });
        lastRequestMeta = {
          status: response.status,
          requestId: readRequestId(response.headers),
        };
        return response;
      } catch (err: unknown) {
        const isTimeout =
          err instanceof DOMException &&
          (err.name === 'TimeoutError' || err.name === 'AbortError');
        if (isTimeout) {
          console.error(`[supabase] storage upload timed out after ${STORAGE_TIMEOUT_MS}ms`);
        } else {
          console.error('[supabase] storage upload failed:', err);
        }
        throw err;
      }
    }

    let lastError: unknown;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        const response = await fetch(input, {
          ...init,
          signal: AbortSignal.timeout(TIMEOUT_MS),
        });

        lastRequestMeta = {
          status: response.status,
          requestId: readRequestId(response.headers),
        };

        // A response means the network is reachable — clear any prior offline flag.
        signalNetworkSuccess();

        // A 401 on a data request may mean the access token expired and the
        // auto-refresh timer hasn't fired yet. Kick a single non-blocking check:
        // a genuinely-failed refresh dispatches a session-expired event, and a
        // successful refresh retries a rejected WRITE with the fresh token.
        if (response.status === 401) {
          const retried = await maybeHandleAuthFailure(input, init);
          if (retried) {
            lastRequestMeta = {
              status: retried.status,
              requestId: readRequestId(retried.headers),
            };
            if (retried.status !== 401) return retried;
            // 692 [2]: a write that still 401s after a successful refresh is
            // genuine session loss mid-action — route it through the SAME expiry
            // path the auth event uses so the draft is snapshotted and the
            // "session expired" notice shows (instead of a plain save failure).
            window.dispatchEvent(new CustomEvent('elkaway:session-expired'));
            return retried;
          }
        }

        return response;
      } catch (err: unknown) {
        lastError = err;

        const isTimeout =
          err instanceof DOMException &&
          (err.name === 'TimeoutError' || err.name === 'AbortError');

        if (isTimeout) {
          const timeoutError = new Error(
            `Database request timed out after ${TIMEOUT_MS}ms`
          );
          timeoutError.name = 'SupabaseTimeoutError';
          (timeoutError as Error & { status: number }).status = 408;
          throw timeoutError;
        }

        const isNetworkError =
          err instanceof TypeError &&
          (err.message === 'Failed to fetch' ||
            err.message === 'NetworkError' ||
            err.message === 'Load failed');

        if (isNetworkError) {
          // A genuine network drop — signal the app so it can show the offline banner.
          signalNetworkError();
        }

        if (!isNetworkError || attempt === MAX_RETRIES) {
          throw err;
        }

        const delay = RETRY_DELAY_MS * Math.pow(2, attempt);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }

    throw lastError;
  };
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
  global: {
    fetch: createRetryFetch(),
  },
});

export async function getCurrentUser() {
  const { data, error } = await supabase.auth.getUser();
  if (error) return null;
  return data.user;
}

export async function signInWithPassword(email: string, password: string) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  return { data, error };
}

// Shared redirect target for the signup confirmation email (and its resends).
// Carry the original destination (e.g. an invite link /join/CODE) through the
// email so a link-click verification returns the user to the trip instead of
// dropping them on the dashboard. React Router state is lost when the link
// opens in a fresh tab, so the destination rides in the URL instead.
export function buildVerifyEmailRedirect(from?: string): string {
  const base = `${window.location.origin}${BASE_PATH}/verify-email`;
  return from ? `${base}?next=${encodeURIComponent(from)}` : base;
}

// 1201 — Languages the Supabase auth-mail templates are written for. The value
// is stored on the auth user's metadata (`options.data.lang`) at signUp and kept
// in sync on an explicit language switch, so the Confirm-signup / Reset-password
// templates can branch on `{{ .Data.lang }}`. Anything unknown falls back to en.
const AUTH_MAIL_LANGS = ['en', 'ko', 'ja', 'zh', 'de', 'es', 'fr'] as const;

function currentAuthLang(): string {
  try {
    // An explicit user choice is written to `i18nextLng` by persistLanguage().
    const stored = (window.localStorage.getItem('i18nextLng') || '').split('-')[0].toLowerCase();
    if ((AUTH_MAIL_LANGS as readonly string[]).includes(stored)) return stored;
  } catch {
    // storage unavailable (private mode) — fall through to navigator
  }
  const nav = (navigator.language || '').split('-')[0].toLowerCase();
  if ((AUTH_MAIL_LANGS as readonly string[]).includes(nav)) return nav;
  return 'en';
}

export async function signUpWithPassword(email: string, password: string, from?: string) {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      emailRedirectTo: buildVerifyEmailRedirect(from),
      // 1201 — carry the current UI language so the auth emails can localize.
      data: { lang: currentAuthLang() },
    },
  });
  return { data, error };
}

// Resend the signup confirmation email while keeping the link pointed at
// /verify-email (instead of the Auth default, which can drop the user at the
// site root and strand them with a code they may not have received).
export async function resendSignupVerification(email: string) {
  const { data, error } = await supabase.auth.resend({
    type: 'signup',
    email,
    options: { emailRedirectTo: buildVerifyEmailRedirect() },
  });
  return { data, error };
}

// 1193 — OAuth callback URL for Google sign-in. Built from the CURRENT origin
// so it matches whichever host the user started from (elkaway.com vs
// www.elkaway.com) — the caller must allow-list both in the Backend Auth
// Redirect URLs. `next` carries the original internal screen so the callback
// can send the user back (validated again on arrival).
export function buildAuthCallbackRedirect(next?: string): string {
  const base = `${window.location.origin}${BASE_PATH}/auth/callback`;
  return next ? `${base}?next=${encodeURIComponent(next)}` : base;
}

// 1193 — Start Google sign-in. The browser is redirected to Google, then back
// to /auth/callback which finishes the session and returns the user to `next`.
export async function signInWithGoogle(next?: string) {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: buildAuthCallbackRedirect(next),
      // 1209 — always show the Google account chooser, even when the browser
      // already has an active Google session (prevents silent auto-pick).
      queryParams: { prompt: 'select_account' },
    },
  });
  return { data, error };
}

export async function signOut() {
  const { error } = await supabase.auth.signOut();
  return { error };
}

/**
 * Raised when a request fails with 401 even after a forced session refresh —
 * i.e. the refresh token itself is gone. Callers should sign the user out and
 * guide them back to login instead of showing a bare "Unauthorized".
 */
export class SessionExpiredError extends Error {
  constructor() {
    super('SESSION_EXPIRED');
    this.name = 'SessionExpiredError';
  }
}

/**
 * Returns a guaranteed-fresh access token.
 *
 * `getSession()` only reads the locally-stored session and does NOT refresh it,
 * so when the auto-refresh timer was throttled (background tab / locked screen)
 * the stored token can already be expired. We check `expires_at` ourselves and
 * force a `refreshSession()` when the token is close to or past expiry.
 */
export async function getValidAccessToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  const expiresAt = session?.expires_at ?? 0;
  const nowSec = Math.floor(Date.now() / 1000);

  if (session?.access_token && expiresAt > nowSec + 5) {
    return session.access_token;
  }

  const { data: refreshed, error } = await supabase.auth.refreshSession();
  if (error || !refreshed.session) return null;
  return refreshed.session.access_token;
}

/**
 * `fetch` that always attaches a fresh access token, and on a 401 performs a
 * single forced refresh + retry.
 *
 * `SessionExpiredError` is thrown ONLY when the refresh itself fails (the
 * refresh token is gone). A 401 that persists after a *successful* refresh is
 * NOT a session problem — it means the token is fine but the request was
 * rejected for another reason (e.g. permission). In that case we return the
 * response so the caller can surface a friendly error WITHOUT signing the user
 * out.
 */
export async function authorizedFetch(
  url: string,
  init: Parameters<typeof fetch>[1] = {}
): Promise<Response> {
  let token = await getValidAccessToken();
  if (!token) throw new SessionExpiredError();

  const makeRequest = (tok: string) =>
    fetch(url, {
      ...init,
      headers: {
        ...(init.headers || {}),
        apikey: supabaseAnonKey,
        Authorization: `Bearer ${tok}`,
      },
    });

  let response = await makeRequest(token);

  if (response.status === 401) {
    // Force a refresh. Only a FAILED refresh is genuine session expiry.
    const { data: refreshed, error } = await supabase.auth.refreshSession();
    if (error || !refreshed.session) {
      throw new SessionExpiredError();
    }
    token = refreshed.session.access_token;
    response = await makeRequest(token);
    // If still 401 after a successful refresh, return it as-is (permission /
    // other rejection) — do NOT treat it as session expiry.
  }

  return response;
}

// Re-validate the session when the tab returns to the foreground. Browsers
// throttle timers in background tabs (and can suspend them entirely while the
// screen is locked), so the auto-refresh timer may never fire — leaving the
// app with an expired token. On visibility/focus, proactively refresh if needed.
if (typeof window !== 'undefined') {
  const revalidate = () => {
    if (document.visibilityState === 'visible') {
      getValidAccessToken().catch(() => {});
    }
  };
  document.addEventListener('visibilitychange', revalidate);
  window.addEventListener('focus', revalidate);
}